import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  FileText, 
  FileSpreadsheet, 
  Download, 
  Loader2, 
  CalendarIcon,
  Building2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency, type Currency } from "@/lib/currency";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subYears } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

interface FinancialStatementProps {
  merchantId: string;
  merchantInfo: {
    store_name: string;
    shopify_store_domain: string;
    business_address?: string | null;
    gst_number?: string | null;
    business_email?: string | null;
    business_phone?: string | null;
  };
  currency?: Currency;
  isProfessional?: boolean;
}

type ReportType = "payment_history" | "cancellation_fees" | "shakatah_dues";
type DatePreset = "this_month" | "last_month" | "this_year" | "last_year" | "custom";
type ExportFormat = "pdf" | "excel";

interface SubscriptionPayment {
  id: string;
  invoice_number: string;
  plan: string;
  billing_cycle: string;
  amount: number;
  status: string;
  paid_at: string | null;
  created_at: string;
}

interface CancellationFee {
  id: string;
  fee_amount: number;
  merchant_share: number;
  shakatah_share: number;
  status: string;
  created_at: string;
  product_name?: string;
  customer_name?: string;
}

interface ShakatahInvoice {
  id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total_amount: number;
  status: string;
  paid_at: string | null;
  created_at: string;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function FinancialStatement({ merchantId, merchantInfo, currency = 'USD', isProfessional = false }: FinancialStatementProps) {
  const [reportType, setReportType] = useState<ReportType>("payment_history");
  const [datePreset, setDatePreset] = useState<DatePreset>("this_month");
  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf");
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(undefined);
  const [generating, setGenerating] = useState(false);

  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();
    switch (datePreset) {
      case "this_month":
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case "last_month":
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case "this_year":
        return { start: startOfYear(now), end: endOfYear(now) };
      case "last_year":
        const lastYear = subYears(now, 1);
        return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
      case "custom":
        return { 
          start: customStartDate || startOfMonth(now), 
          end: customEndDate || endOfMonth(now) 
        };
      default:
        return { start: startOfMonth(now), end: endOfMonth(now) };
    }
  };

  const getPeriodLabel = () => {
    const { start, end } = getDateRange();
    return `${format(start, "dd MMM yyyy")} - ${format(end, "dd MMM yyyy")}`;
  };

  const getReportTypeLabel = () => {
    switch (reportType) {
      case "payment_history":
        return "Subscription Payment History";
      case "cancellation_fees":
        return "Cancellation Fee Statement";
      case "shakatah_dues":
        return "Shakatah Dues & Payments";
      default:
        return "Financial Statement";
    }
  };

  const fetchPaymentHistory = async (startDate: Date, endDate: Date): Promise<SubscriptionPayment[]> => {
    const { data, error } = await supabase
      .from("subscription_payments")
      .select("*")
      .eq("merchant_id", merchantId)
      .gte("created_at", startDate.toISOString())
      .lte("created_at", endDate.toISOString())
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  };

  const fetchCancellationFees = async (startDate: Date, endDate: Date): Promise<CancellationFee[]> => {
    const { data, error } = await supabase
      .from("cancellation_fees")
      .select("*, split_payment_requests(product_name, customer_name)")
      .eq("merchant_id", merchantId)
      .gte("created_at", startDate.toISOString())
      .lte("created_at", endDate.toISOString())
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data || []).map(f => ({
      ...f,
      product_name: (f as any).split_payment_requests?.product_name,
      customer_name: (f as any).split_payment_requests?.customer_name,
    }));
  };

  const fetchShakatahInvoices = async (startDate: Date, endDate: Date): Promise<ShakatahInvoice[]> => {
    const { data, error } = await supabase
      .from("shakatah_invoices")
      .select("*")
      .eq("merchant_id", merchantId)
      .gte("created_at", startDate.toISOString())
      .lte("created_at", endDate.toISOString())
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  };

  const fetchPendingDues = async (): Promise<number> => {
    const { data, error } = await supabase
      .from("cancellation_fees")
      .select("shakatah_share")
      .eq("merchant_id", merchantId)
      .eq("status", "pending");

    if (error) throw error;
    return (data || []).reduce((sum, f) => sum + Number(f.shakatah_share), 0);
  };

  const generatePDF = async () => {
    const { start, end } = getDateRange();
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header with company branding
    doc.setFillColor(99, 102, 241); // Indigo
    doc.rect(0, 0, pageWidth, 40, "F");
    
    // App Name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("SHAKATAH", 14, 20);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Split Payment Platform", 14, 28);
    
    // Statement Type
    doc.setFontSize(12);
    doc.text(getReportTypeLabel().toUpperCase(), pageWidth - 14, 20, { align: "right" });
    doc.setFontSize(9);
    doc.text(`Generated: ${format(new Date(), "dd MMM yyyy, HH:mm")}`, pageWidth - 14, 28, { align: "right" });

    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Merchant Details Section
    let yPos = 50;
    
    doc.setFillColor(248, 250, 252);
    doc.rect(14, yPos - 6, pageWidth - 28, 34, "F");
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Account Holder Details", 20, yPos);
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    yPos += 8;
    doc.text(`Business Name: ${merchantInfo.store_name}`, 20, yPos);
    yPos += 5;
    doc.text(`Store Domain: ${merchantInfo.shopify_store_domain}`, 20, yPos);
    
    if (merchantInfo.business_address) {
      yPos += 5;
      doc.text(`Address: ${merchantInfo.business_address}`, 20, yPos);
    }
    
    // Right column
    yPos = 58;
    if (merchantInfo.gst_number) {
      doc.text(`GST Number: ${merchantInfo.gst_number}`, pageWidth - 80, yPos);
      yPos += 5;
    }
    if (merchantInfo.business_email) {
      doc.text(`Email: ${merchantInfo.business_email}`, pageWidth - 80, yPos);
      yPos += 5;
    }
    if (merchantInfo.business_phone) {
      doc.text(`Phone: ${merchantInfo.business_phone}`, pageWidth - 80, yPos);
    }

    // Statement Period
    yPos = 92;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Statement Period:", 14, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(getPeriodLabel(), 55, yPos);

    yPos += 10;

    // Fetch and render data based on report type
    if (reportType === "payment_history") {
      const payments = await fetchPaymentHistory(start, end);
      
      // Summary
      const totalPaid = payments.filter(p => p.status === "paid").reduce((sum, p) => sum + p.amount, 0);
      const pendingAmount = payments.filter(p => p.status === "pending").reduce((sum, p) => sum + p.amount, 0);
      
      doc.setFillColor(240, 253, 244);
      doc.rect(14, yPos, (pageWidth - 28) / 2 - 5, 20, "F");
      doc.setFillColor(254, 252, 232);
      doc.rect(14 + (pageWidth - 28) / 2, yPos, (pageWidth - 28) / 2 - 5, 20, "F");
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Total Paid", 20, yPos + 8);
      doc.text("Pending", 14 + (pageWidth - 28) / 2 + 6, yPos + 8);
      
      doc.setFontSize(12);
      doc.text(formatCurrency(totalPaid, currency), 20, yPos + 16);
      doc.text(formatCurrency(pendingAmount, currency), 14 + (pageWidth - 28) / 2 + 6, yPos + 16);
      
      yPos += 30;

      // Transactions table
      autoTable(doc, {
        startY: yPos,
        head: [["Date", "Invoice #", "Plan", "Billing Cycle", "Amount", "Status"]],
        body: payments.map(p => [
          format(new Date(p.paid_at || p.created_at), "dd MMM yyyy"),
          p.invoice_number,
          p.plan.charAt(0).toUpperCase() + p.plan.slice(1),
          p.billing_cycle === "annual" ? "Annual" : "Monthly",
          formatCurrency(p.amount, currency),
          p.status.charAt(0).toUpperCase() + p.status.slice(1)
        ]),
        theme: "striped",
        headStyles: { fillColor: [99, 102, 241], textColor: 255 },
        styles: { fontSize: 8 },
      });

    } else if (reportType === "cancellation_fees") {
      const fees = await fetchCancellationFees(start, end);
      
      const totalFees = fees.reduce((sum, f) => sum + f.fee_amount, 0);
      const merchantRevenue = fees.reduce((sum, f) => sum + f.merchant_share, 0);
      const shakatahShare = fees.reduce((sum, f) => sum + f.shakatah_share, 0);
      
      // Summary boxes
      const boxWidth = (pageWidth - 28 - 10) / 3;
      
      doc.setFillColor(254, 243, 199);
      doc.rect(14, yPos, boxWidth, 24, "F");
      doc.setFillColor(220, 252, 231);
      doc.rect(14 + boxWidth + 5, yPos, boxWidth, 24, "F");
      doc.setFillColor(219, 234, 254);
      doc.rect(14 + (boxWidth + 5) * 2, yPos, boxWidth, 24, "F");
      
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("Total Fees", 20, yPos + 8);
      doc.text("Your Share (50%)", 20 + boxWidth + 5, yPos + 8);
      doc.text("Shakatah Share (50%)", 20 + (boxWidth + 5) * 2, yPos + 8);
      
      doc.setFontSize(11);
      doc.text(formatCurrency(totalFees, currency), 20, yPos + 18);
      doc.text(formatCurrency(merchantRevenue, currency), 20 + boxWidth + 5, yPos + 18);
      doc.text(formatCurrency(shakatahShare, currency), 20 + (boxWidth + 5) * 2, yPos + 18);
      
      yPos += 34;

      autoTable(doc, {
        startY: yPos,
        head: [["Date", "Product", "Customer", "Fee Amount", "Your Share", "Status"]],
        body: fees.map(f => [
          format(new Date(f.created_at), "dd MMM yyyy"),
          f.product_name || "N/A",
          f.customer_name || "N/A",
          formatCurrency(f.fee_amount, currency),
          formatCurrency(f.merchant_share, currency),
          f.status.charAt(0).toUpperCase() + f.status.slice(1)
        ]),
        theme: "striped",
        headStyles: { fillColor: [99, 102, 241], textColor: 255 },
        styles: { fontSize: 8 },
      });

    } else if (reportType === "shakatah_dues") {
      const invoices = await fetchShakatahInvoices(start, end);
      const pendingDues = await fetchPendingDues();
      
      const totalPaid = invoices.filter(i => i.status === "paid").reduce((sum, i) => sum + i.total_amount, 0);
      
      // Summary boxes
      const boxWidth = (pageWidth - 28 - 5) / 2;
      
      doc.setFillColor(220, 252, 231);
      doc.rect(14, yPos, boxWidth, 24, "F");
      doc.setFillColor(254, 226, 226);
      doc.rect(14 + boxWidth + 5, yPos, boxWidth, 24, "F");
      
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("Total Paid to Shakatah", 20, yPos + 8);
      doc.text("Current Pending Dues", 20 + boxWidth + 5, yPos + 8);
      
      doc.setFontSize(11);
      doc.text(formatCurrency(totalPaid, currency), 20, yPos + 18);
      doc.text(formatCurrency(pendingDues, currency), 20 + boxWidth + 5, yPos + 18);
      
      yPos += 34;
      
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text("* Pending dues will be included in your next monthly invoice", 14, yPos);
      doc.setTextColor(0, 0, 0);
      
      yPos += 10;

      autoTable(doc, {
        startY: yPos,
        head: [["Invoice #", "Period", "Total Fees", "Amount", "Status", "Paid On"]],
        body: invoices.map(i => [
          i.invoice_number,
          `${format(new Date(i.period_start), "dd MMM")} - ${format(new Date(i.period_end), "dd MMM yyyy")}`,
          i.total_amount.toString(),
          formatCurrency(i.total_amount, currency),
          i.status.charAt(0).toUpperCase() + i.status.slice(1),
          i.paid_at ? format(new Date(i.paid_at), "dd MMM yyyy") : "-"
        ]),
        theme: "striped",
        headStyles: { fillColor: [99, 102, 241], textColor: 255 },
        styles: { fontSize: 8 },
      });
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `This is a computer-generated statement and does not require a signature.`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 15,
        { align: "center" }
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    // Download
    const fileName = `${merchantInfo.store_name.replace(/\s+/g, "_")}_${reportType}_${format(start, "yyyyMMdd")}_${format(end, "yyyyMMdd")}.pdf`;
    doc.save(fileName);
  };

  const generateExcel = async () => {
    const { start, end } = getDateRange();
    const wb = XLSX.utils.book_new();

    // Info sheet
    const infoData = [
      ["SHAKATAH - Split Payment Platform"],
      [""],
      ["FINANCIAL STATEMENT"],
      [""],
      ["Report Type:", getReportTypeLabel()],
      ["Statement Period:", getPeriodLabel()],
      ["Generated On:", format(new Date(), "dd MMM yyyy, HH:mm")],
      [""],
      ["ACCOUNT HOLDER DETAILS"],
      ["Business Name:", merchantInfo.store_name],
      ["Store Domain:", merchantInfo.shopify_store_domain],
      ["Address:", merchantInfo.business_address || "N/A"],
      ["GST Number:", merchantInfo.gst_number || "N/A"],
      ["Email:", merchantInfo.business_email || "N/A"],
      ["Phone:", merchantInfo.business_phone || "N/A"],
    ];
    const infoSheet = XLSX.utils.aoa_to_sheet(infoData);
    XLSX.utils.book_append_sheet(wb, infoSheet, "Statement Info");

    // Data sheet based on report type
    if (reportType === "payment_history") {
      const payments = await fetchPaymentHistory(start, end);
      
      const summaryData = [
        ["SUBSCRIPTION PAYMENT HISTORY"],
        [""],
        ["Summary"],
        ["Total Paid:", formatCurrency(payments.filter(p => p.status === "paid").reduce((sum, p) => sum + p.amount, 0), currency)],
        ["Pending:", formatCurrency(payments.filter(p => p.status === "pending").reduce((sum, p) => sum + p.amount, 0), currency)],
        ["Total Transactions:", payments.length.toString()],
        [""],
      ];
      
      const headers = ["Date", "Invoice Number", "Plan", "Billing Cycle", `Amount (${currency})`, "Status"];
      const rows = payments.map(p => [
        format(new Date(p.paid_at || p.created_at), "dd MMM yyyy"),
        p.invoice_number,
        p.plan.charAt(0).toUpperCase() + p.plan.slice(1),
        p.billing_cycle === "annual" ? "Annual" : "Monthly",
        p.amount,
        p.status.charAt(0).toUpperCase() + p.status.slice(1)
      ]);
      
      const dataSheet = XLSX.utils.aoa_to_sheet([...summaryData, headers, ...rows]);
      XLSX.utils.book_append_sheet(wb, dataSheet, "Payment History");

    } else if (reportType === "cancellation_fees") {
      const fees = await fetchCancellationFees(start, end);
      
      const summaryData = [
        ["CANCELLATION FEE STATEMENT"],
        [""],
        ["Summary"],
        ["Total Fees:", formatCurrency(fees.reduce((sum, f) => sum + f.fee_amount, 0), currency)],
        ["Your Share (50%):", formatCurrency(fees.reduce((sum, f) => sum + f.merchant_share, 0), currency)],
        ["Shakatah Share (50%):", formatCurrency(fees.reduce((sum, f) => sum + f.shakatah_share, 0), currency)],
        ["Total Records:", fees.length.toString()],
        [""],
      ];
      
      const headers = ["Date", "Product", "Customer", `Fee Amount (${currency})`, `Your Share (${currency})`, `Shakatah Share (${currency})`, "Status"];
      const rows = fees.map(f => [
        format(new Date(f.created_at), "dd MMM yyyy"),
        f.product_name || "N/A",
        f.customer_name || "N/A",
        f.fee_amount,
        f.merchant_share,
        f.shakatah_share,
        f.status.charAt(0).toUpperCase() + f.status.slice(1)
      ]);
      
      const dataSheet = XLSX.utils.aoa_to_sheet([...summaryData, headers, ...rows]);
      XLSX.utils.book_append_sheet(wb, dataSheet, "Cancellation Fees");

    } else if (reportType === "shakatah_dues") {
      const invoices = await fetchShakatahInvoices(start, end);
      const pendingDues = await fetchPendingDues();
      
      const summaryData = [
        ["SHAKATAH DUES & PAYMENTS"],
        [""],
        ["Summary"],
        ["Total Paid to Shakatah:", formatCurrency(invoices.filter(i => i.status === "paid").reduce((sum, i) => sum + i.total_amount, 0), currency)],
        ["Current Pending Dues:", formatCurrency(pendingDues, currency)],
        ["Note:", "Pending dues will be included in your next monthly invoice"],
        [""],
      ];
      
      const headers = ["Invoice Number", "Period Start", "Period End", `Amount (${currency})`, "Status", "Paid On"];
      const rows = invoices.map(i => [
        i.invoice_number,
        format(new Date(i.period_start), "dd MMM yyyy"),
        format(new Date(i.period_end), "dd MMM yyyy"),
        i.total_amount,
        i.status.charAt(0).toUpperCase() + i.status.slice(1),
        i.paid_at ? format(new Date(i.paid_at), "dd MMM yyyy") : "-"
      ]);
      
      const dataSheet = XLSX.utils.aoa_to_sheet([...summaryData, headers, ...rows]);
      XLSX.utils.book_append_sheet(wb, dataSheet, "Shakatah Dues");
    }

    // Download
    const fileName = `${merchantInfo.store_name.replace(/\s+/g, "_")}_${reportType}_${format(start, "yyyyMMdd")}_${format(end, "yyyyMMdd")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleGenerate = async () => {
    if (datePreset === "custom" && (!customStartDate || !customEndDate)) {
      toast.error("Please select both start and end dates for custom range");
      return;
    }

    if (datePreset === "custom" && customStartDate && customEndDate && customStartDate > customEndDate) {
      toast.error("Start date cannot be after end date");
      return;
    }

    setGenerating(true);
    try {
      if (exportFormat === "pdf") {
        await generatePDF();
      } else {
        await generateExcel();
      }
      toast.success(`Statement downloaded successfully`);
    } catch (error: any) {
      console.error("Error generating statement:", error);
      toast.error(error.message || "Failed to generate statement");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Download Financial Statement
        </CardTitle>
        <CardDescription>
          Generate detailed statements like bank account summaries for your records
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Report Type */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Report Type</Label>
          <Select value={reportType} onValueChange={(v) => setReportType(v as ReportType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="payment_history">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Subscription Payment History</span>
                </div>
              </SelectItem>
              <SelectItem value="cancellation_fees">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Cancellation Fee Statement</span>
                </div>
              </SelectItem>
              <SelectItem value="shakatah_dues">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Shakatah Dues & Payments</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Date Range */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Statement Period</Label>
          <Select value={datePreset} onValueChange={(v) => setDatePreset(v as DatePreset)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Date Range</SelectItem>
            </SelectContent>
          </Select>

          {datePreset === "custom" && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !customStartDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customStartDate ? format(customStartDate, "dd MMM yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customStartDate}
                      onSelect={setCustomStartDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !customEndDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customEndDate ? format(customEndDate, "dd MMM yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customEndDate}
                      onSelect={setCustomEndDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
        </div>

        {/* Export Format */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Export Format</Label>
          <RadioGroup
            value={exportFormat}
            onValueChange={(v) => setExportFormat(v as ExportFormat)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="pdf" id="pdf" />
              <Label htmlFor="pdf" className="flex items-center gap-2 cursor-pointer">
                <FileText className="h-4 w-4 text-red-500" />
                PDF Document
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="excel" id="excel" />
              <Label htmlFor="excel" className="flex items-center gap-2 cursor-pointer">
                <FileSpreadsheet className="h-4 w-4 text-green-600" />
                Excel Spreadsheet
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Preview Info */}
        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
          <p className="text-sm font-medium">Statement Preview</p>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Report: {getReportTypeLabel()}</p>
            <p>• Period: {getPeriodLabel()}</p>
            <p>• Format: {exportFormat === "pdf" ? "PDF Document" : "Excel Spreadsheet"}</p>
          </div>
        </div>

        {/* Generate Button */}
        <Button 
          onClick={handleGenerate} 
          disabled={generating}
          className={`w-full ${isProfessional ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600' : ''}`}
          size="lg"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating Statement...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Download Statement
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
